import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="text-base sm:text-lg font-bold text-[#1800ad] mb-2">{n}. {title}</h2>
    {children}
  </section>
);

const SubHeading = ({ children }) => (
  <h3 className="font-bold text-[#ff8500] mt-3 mb-1 text-sm">{children}</h3>
);

const PrivacyPage = () => {
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(160deg, #fff5f0 0%, #f0edff 50%, #fff5f0 100%)' }}>
      {/* Header Bar — solid blue, compact height */}
      <div className="py-5 sm:py-7 relative overflow-hidden" style={{ background: '#1800ad' }}>
        <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 20% 50%, #fff 0%, transparent 40%)' }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-8 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-xs sm:text-sm font-medium mb-2 sm:mb-3 transition-colors" data-testid="back-link">
            <ArrowLeft size={14} /> Back to Home
          </Link>
          <div className="flex items-center gap-2.5">
            <Shield size={22} className="text-white sm:w-6 sm:h-6" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white" data-testid="privacy-title">Privacy Policy</h1>
          </div>
          <p className="text-white/70 text-[11px] sm:text-xs mt-1.5">Your data, your control. Last Updated: June 2026.</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-8 -mt-4 sm:-mt-6">
        <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 lg:p-12 shadow-lg border border-[#ff8500]/5" data-testid="privacy-content">
          <div className="space-y-5 sm:space-y-7 text-xs sm:text-sm text-[#4B5563] leading-relaxed">

            <Section n={1} title="Introduction">
              <p>Welcome to GenzoX (&quot;Platform&quot;, &quot;GenzoX&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;).</p>
              <p className="mt-2">Your privacy is important to us. This Privacy Policy explains how GenzoX collects, uses, stores, shares, and protects your personal information when you access or use our website, mobile applications, services, and related features.</p>
              <p className="mt-2">By using GenzoX, you consent to the collection and processing of your information as described in this Privacy Policy.</p>
            </Section>

            <Section n={2} title="Information We Collect">
              <SubHeading>2.1 Account Information</SubHeading>
              <p>When you create or use an account, we may collect:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Full Name</li>
                <li>Mobile Number</li>
                <li>Email Address</li>
                <li>Profile Photo</li>
                <li>User ID</li>
                <li>Account Preferences</li>
              </ul>

              <SubHeading>2.2 Authentication Information</SubHeading>
              <p>When using Google Sign-In or other authentication services, we may collect:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Google Account Name</li>
                <li>Google Email Address</li>
                <li>Google Profile Picture</li>
                <li>Authentication Tokens necessary for login</li>
              </ul>
              <p className="mt-2">GenzoX does not collect your Google password.</p>

              <SubHeading>2.3 Dealer Information</SubHeading>
              <p>If you purchase Dealer Plans or Property Dealer Plans, we may collect:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Enterprise Name</li>
                <li>Business Name</li>
                <li>Alternate Contact Number</li>
                <li>Dealer Category</li>
                <li>Dealer Plan Information</li>
              </ul>
              <p className="mt-2">This information is used solely for account administration, plan management, and dealer-related features.</p>

              <SubHeading>2.4 Listing Information</SubHeading>
              <p>When posting advertisements, we may collect:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Listing Title</li>
                <li>Description</li>
                <li>Category</li>
                <li>Subcategory</li>
                <li>Brand</li>
                <li>Model</li>
                <li>Price</li>
                <li>Images</li>
                <li>Listing Status</li>
                <li>Listing Metadata</li>
              </ul>

              <SubHeading>2.5 Location Information</SubHeading>
              <p>We may collect:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>GPS Location</li>
                <li>Device Location</li>
                <li>Area Name</li>
                <li>Locality Name</li>
                <li>City Name</li>
                <li>State Name</li>
                <li>PIN Code</li>
                <li>User-Selected Map Location</li>
              </ul>
              <p className="mt-2 font-semibold">Location data may be collected when:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Posting an ad</li>
                <li>Editing an ad</li>
                <li>Using &quot;Use My Current Location&quot;</li>
                <li>Searching for nearby listings</li>
              </ul>

              <SubHeading>2.6 Chat & Messaging Information</SubHeading>
              <p>When using GenzoX chat features, we may collect:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Chat Messages</li>
                <li>Offers Sent</li>
                <li>Offers Received</li>
                <li>Message Timestamps</li>
                <li>Conversation Metadata</li>
              </ul>
              <p className="mt-2">Chat information may be stored to facilitate communication, prevent abuse, resolve disputes, and improve platform functionality.</p>

              <SubHeading>2.7 Device & Technical Information</SubHeading>
              <p>We may automatically collect:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Device Type</li>
                <li>Browser Type</li>
                <li>Operating System</li>
                <li>IP Address</li>
                <li>Device Identifiers</li>
                <li>Language Preferences</li>
                <li>Session Information</li>
                <li>Log Data</li>
              </ul>

              <SubHeading>2.8 Payment Information</SubHeading>
              <p>When purchasing plans or services, payments are processed through third-party payment providers. GenzoX does not store complete debit card, credit card, UPI PIN, or banking credentials.</p>
              <p className="mt-2 font-semibold">We may receive:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Payment Status</li>
                <li>Transaction ID</li>
                <li>Purchase Information</li>
                <li>Subscription Information</li>
              </ul>
            </Section>

            <Section n={3} title="How We Use Information">
              <p>We use information to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Create and manage accounts</li>
                <li>Authenticate users</li>
                <li>Verify mobile numbers</li>
                <li>Publish listings</li>
                <li>Display advertisements</li>
                <li>Enable chat functionality</li>
                <li>Process subscriptions</li>
                <li>Process dealer plans</li>
                <li>Deliver notifications</li>
                <li>Improve search results</li>
                <li>Prevent fraud</li>
                <li>Enforce platform policies</li>
                <li>Comply with legal obligations</li>
              </ul>
            </Section>

            <Section n={4} title="Notifications & Communications">
              <p>GenzoX may send:</p>
              <SubHeading>Transactional Notifications</SubHeading>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>OTP Verification Messages</li>
                <li>Account Notifications</li>
                <li>Listing Status Updates</li>
                <li>Ad Approval Notifications</li>
                <li>Ad Expiry Notifications</li>
                <li>Plan Expiry Notifications</li>
                <li>Restoration Notifications</li>
              </ul>
              <SubHeading>Promotional Notifications</SubHeading>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>New Features</li>
                <li>Plan Recommendations</li>
                <li>Promotional Offers</li>
                <li>Marketing Campaigns</li>
              </ul>
              <SubHeading>Chat Notifications</SubHeading>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>New Messages</li>
                <li>New Offers</li>
                <li>Message Replies</li>
              </ul>
              <p className="mt-2 font-semibold">Notifications may be delivered through:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Email</li>
                <li>SMS</li>
                <li>Browser Notifications</li>
                <li>Push Notifications</li>
                <li>In-App Notifications</li>
              </ul>
            </Section>

            <Section n={5} title="Search & Recommendation Systems">
              <p>GenzoX may use listing and user information to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Improve search results</li>
                <li>Display recommendations</li>
                <li>Provide location-based suggestions</li>
                <li>Personalize content</li>
                <li>Improve user experience</li>
              </ul>
              <p className="mt-2 font-semibold">Search ranking may consider:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Relevance</li>
                <li>Freshness</li>
                <li>Category Matching</li>
                <li>Location Matching</li>
                <li>Promotional Products</li>
                <li>User Activity</li>
              </ul>
            </Section>

            <Section n={6} title="Image Processing, Archiving & Purging">
              <p>Images uploaded to GenzoX may be:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Compressed</li>
                <li>Watermarked</li>
                <li>Stored</li>
                <li>Archived</li>
                <li>Restored</li>
                <li>Deleted</li>
              </ul>
              <p className="mt-2 font-semibold">After listing expiration:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Images may enter an archive period</li>
                <li>Images may later be permanently deleted according to retention policies</li>
              </ul>
              <p className="mt-2">Text-based listing information may be retained separately from images.</p>
              <p className="mt-2">GenzoX does not guarantee indefinite storage of uploaded images.</p>
            </Section>

            <Section n={7} title="Ad Restoration">
              <p>When eligible, GenzoX may provide restoration features for expired listings.</p>
              <p className="mt-2 font-semibold">Restored listings may include:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Listing Information</li>
                <li>Images (if still available)</li>
                <li>Listing Metadata</li>
              </ul>
              <p className="mt-2 font-semibold">Restoration availability depends on:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Retention periods</li>
                <li>Plan eligibility</li>
                <li>Image archive availability</li>
              </ul>
            </Section>

            <Section n={8} title="Fraud Prevention & Security">
              <p>To protect users and the platform, GenzoX may use:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Fraud Detection Systems</li>
                <li>Duplicate Listing Detection</li>
                <li>Automated Monitoring</li>
                <li>AI-Assisted Moderation</li>
                <li>Security Audits</li>
              </ul>
              <p className="mt-2">We may investigate suspicious activity and take enforcement actions when necessary.</p>
            </Section>

            <Section n={9} title="Sharing of Information">
              <p>We do not sell personal information.</p>
              <p className="mt-2">We may share information with:</p>
              <SubHeading>Service Providers</SubHeading>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Hosting Providers</li>
                <li>Cloud Infrastructure Providers</li>
                <li>Analytics Providers</li>
                <li>Payment Processors</li>
                <li>Notification Providers</li>
              </ul>
              <SubHeading>Legal Authorities</SubHeading>
              <p className="font-semibold">When required by:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Applicable laws</li>
                <li>Court orders</li>
                <li>Government requests</li>
                <li>Law enforcement investigations</li>
              </ul>
            </Section>

            <Section n={10} title="Data Retention">
              <p>We retain information for as long as necessary to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Provide services</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes</li>
                <li>Prevent fraud</li>
                <li>Enforce agreements</li>
              </ul>
              <p className="mt-2 font-semibold">Retention periods may vary depending on:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Account status</li>
                <li>Listing status</li>
                <li>Legal requirements</li>
                <li>Security needs</li>
              </ul>

              <SubHeading>10.1 What Happens When You Delete Your Account</SubHeading>
              <p>When you initiate self-service account deletion from within the app (Profile &rarr; My Account &rarr; Delete My Account), the following actions are performed immediately and irreversibly:</p>

              <p className="mt-2 font-semibold">Permanently deleted (cannot be restored):</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Your user profile (name, email, profile photo, preferences)</li>
                <li>All your active and inactive listings</li>
                <li>Your entire chat conversation history and messages</li>
                <li>Your wishlist and saved searches</li>
                <li>All active sessions across every device (you are signed out everywhere)</li>
                <li>Push-notification subscriptions and device registrations</li>
                <li>Subscription records and add-on purchase entitlements</li>
                <li>Reviews authored by you</li>
                <li>OTP records associated with your phone and email</li>
                <li>Blocked-user lists you maintained</li>
              </ul>

              <p className="mt-2 font-semibold">Anonymized (PII removed, structural records retained):</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li><strong>Payment and invoice records</strong> &mdash; required under the Indian Income Tax Act, GST Act, and Companies Act (typically 7 years). Your identity is replaced with an anonymous reference identifier.</li>
                <li><strong>Contact-form submissions</strong> previously sent to our support team &mdash; retained as part of our customer-service records, with your identity anonymized.</li>
                <li><strong>Abuse reports you filed</strong> against other users &mdash; retained to preserve moderation context, with your identity anonymized.</li>
                <li><strong>Administrative audit logs and moderation records</strong> &mdash; retained for safety, compliance, and dispute-resolution purposes.</li>
              </ul>

              <p className="mt-2">After deletion is complete, you will receive an on-screen confirmation and be automatically signed out and redirected to the home screen. The deletion is final and cannot be reversed by us.</p>
              <p className="mt-2">If you wish to use GenzoX again in the future, you are welcome to create a fresh account with the same or a different phone number, email, or Apple/Google account.</p>
            </Section>

            <Section n={11} title="Cookies & Similar Technologies">
              <p>GenzoX may use:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Cookies</li>
                <li>Session Cookies</li>
                <li>Local Storage</li>
                <li>Analytics Technologies</li>
              </ul>
              <p className="mt-2 font-semibold">These technologies help:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Maintain sessions</li>
                <li>Improve performance</li>
                <li>Remember preferences</li>
                <li>Analyze usage</li>
              </ul>
              <p className="mt-2">Users may disable cookies through browser settings, though some features may not function properly.</p>

              <SubHeading>Advertising &amp; Ad Personalisation (Google AdSense)</SubHeading>
              <p>GenzoX displays third-party advertisements on its Homepage through <strong>Google AdSense</strong>. Google and its partners may use cookies and similar technologies to serve ads and, where applicable, to personalise them based on your prior visits to this and other websites.</p>
              <ul className="list-disc pl-5 space-y-1 mt-2">
                <li>Third-party vendors, including Google, use cookies to serve ads based on a user&apos;s previous visits to GenzoX or other websites.</li>
                <li>Google&apos;s use of advertising cookies enables it and its partners to serve ads to users based on their visit to GenzoX and/or other sites on the Internet.</li>
                <li>You can opt out of personalised advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-[#1800ad] underline">Google Ads Settings</a>, or opt out of third-party vendors&apos; use of cookies for personalised advertising at <a href="https://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer" className="text-[#1800ad] underline">www.aboutads.info/choices</a>.</li>
              </ul>
              <p className="mt-2"><strong>EU/EEA, UK &amp; consent:</strong> Where required by law (including the GDPR and applicable e-privacy rules), ads and ad-personalisation cookies are used only where a valid legal basis or your consent applies. Users in these regions may be shown a consent request and can grant, refuse or withdraw consent for personalised advertising at any time; refusing does not affect your ability to use GenzoX, and non-personalised ads may still be shown. Google Auto Ads are not used, and advertisements appear only on the Homepage.</p>
            </Section>

            <Section n={12} title="User Rights">
              <p>Subject to applicable laws, users may request:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Access to personal information</li>
                <li>Correction of inaccurate information</li>
                <li>Deletion of eligible information</li>
                <li>Withdrawal of consent where applicable</li>
                <li>Closure of accounts</li>
              </ul>
              <p className="mt-2">Certain information may be retained where required by law or legitimate business purposes.</p>

              <SubHeading>12.1 Self-Service Account Deletion</SubHeading>
              <p>You have the right to permanently delete your GenzoX account at any time, directly from within the app or website, without needing to contact support.</p>
              <p className="mt-2 font-semibold">How to delete your account:</p>
              <ol className="list-decimal pl-5 space-y-1 mt-1">
                <li>Sign in to your GenzoX account</li>
                <li>Navigate to <strong>Profile &rarr; My Account</strong></li>
                <li>Scroll to the <strong>Delete Account</strong> section</li>
                <li>Tap <strong>&quot;Delete My Account&quot;</strong></li>
                <li>Review the consequences listed in the confirmation screen</li>
                <li>Type <strong>DELETE</strong> in the confirmation field</li>
                <li>Tap <strong>&quot;Delete Account&quot;</strong> to finalize</li>
              </ol>
              <p className="mt-2">For details of exactly which data is permanently removed versus anonymized for legal-retention purposes, please refer to <strong>Section 10.1 &mdash; What Happens When You Delete Your Account</strong> above.</p>
              <p className="mt-2">If you are unable to access your account (for example, you lost access to your registered phone number or email and cannot sign in), you may also request deletion by emailing <a href="mailto:help@genzox.in" className="text-[#1800ad] font-bold hover:underline">help@genzox.in</a> from your registered email address. We will verify your identity and complete the deletion within <strong>30 days</strong> of receiving the request.</p>
              <p className="mt-2 italic">Note: Account deletion is permanent and cannot be reversed. Active subscriptions are cancelled at the time of deletion and are non-refundable unless otherwise required by applicable law.</p>
            </Section>

            <Section n={13} title="Children's Privacy">
              <p>GenzoX is not intended for individuals under 18 years of age.</p>
              <p className="mt-2">We do not knowingly collect personal information from children.</p>
              <p className="mt-2">If such information is identified, we may remove it.</p>
            </Section>

            <Section n={14} title="Third-Party Services">
              <p>GenzoX may integrate with:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Google Sign-In</li>
                <li>Google Maps</li>
                <li>Payment Providers</li>
                <li>Notification Services</li>
                <li>Analytics Providers</li>
              </ul>
              <p className="mt-2">These services operate under their own privacy policies.</p>
              <p className="mt-2">GenzoX is not responsible for third-party privacy practices.</p>
            </Section>

            <Section n={15} title="Security Measures">
              <p>We implement reasonable technical and organizational safeguards to protect information, including:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Access Controls</li>
                <li>Authentication Mechanisms</li>
                <li>Secure Data Storage</li>
                <li>Monitoring Systems</li>
              </ul>
              <p className="mt-2">However, no system can guarantee absolute security.</p>
            </Section>

            <Section n={16} title="Changes to Privacy Policy">
              <p>GenzoX may update this Privacy Policy periodically.</p>
              <p className="mt-2">Updated versions will be published on the Platform.</p>
              <p className="mt-2">Continued use of GenzoX after updates constitutes acceptance of the revised Privacy Policy.</p>
            </Section>

            <Section n={17} title="Rights Under Applicable Indian Data Protection Laws">
              <p>Where applicable under Indian law, including the Digital Personal Data Protection Act, 2023 (DPDP Act), users may exercise rights relating to:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Access</li>
                <li>Correction</li>
                <li>Deletion</li>
                <li>Consent Withdrawal</li>
                <li>Grievance Redressal</li>
              </ul>
              <p className="mt-2">Requests may be submitted using the contact information provided below.</p>
            </Section>

            <Section n={18} title="Contact & Grievance Officer">
              <p>For privacy-related requests, complaints, legal inquiries, or grievances, contact:</p>
              <p className="mt-2">Email: <a href="mailto:help@genzox.in" className="text-[#1800ad] font-semibold hover:text-[#ff8500]">help@genzox.in</a></p>
              <p className="mt-2">GenzoX will process requests in accordance with applicable laws and regulatory requirements.</p>
            </Section>

            <Section n={19} title="Consent">
              <p>By accessing or using GenzoX, you acknowledge that you have read, understood, and agreed to this Privacy Policy.</p>
            </Section>

          </div>
        </div>

        {/* Footer spacer */}
        <div className="py-8 text-center">
          <Link to="/terms" className="text-[#1800ad] hover:text-[#ff8500] text-sm font-semibold transition-colors" data-testid="go-to-terms">
            Read our Terms &amp; Conditions &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
